import { PlatformType } from '@microsoft/office-js-helpers'
import { DateTime } from 'luxon'
import { refreshAuthData, ServerSession } from '../session'
import { LoftMessages, getLoftMessages, notificationTypes } from './constants'
import { onPromiseTimeout } from '../../office/utils/promise'
import { getMessageIds } from '../../office/item'
import { melodyRequest, publicRequest } from '../../gateway/request'
import { LoftAsyncProps, LoftRecord } from './record'
import {
  LoftServices,
  LoftFromOutlookResponse,
  FindByEmailResponse,
  Recipient,
  Guest,
} from '../../types/loft'
import { Me } from '../../types/me'
import { insertOutlookCustomData } from '../../actions/insertToOutlook'
import { refreshLocaleData } from '../../platform/localization'
import { getAccountId, getCadenceId, getStepId, resolveRecipientName } from '../loftCalendarEvents'
import { isDesktopWindows } from '../../office/client'
import { getPersonByEmail } from '../people'
import { OfficeWrapper } from '../../office/office'
import { addBreadcrumb, addEventProcessor, setContext, setUser } from '../../sentry-config'
import { resolveTrackingPreferences, calculateOverrideDisabled } from '../trackingPreferences'
import { CustomProperties } from '../../types/customProperties'

export const BACKEND_AZURE = 'backend-azure'
export const FRONTEND_EWS = 'frontend-ews'
export const CALENDAR_LOFTING = 'CALENDAR_LOFTING'

const emptyRecipients = {
  to: [],
  cc: [],
  bcc: [],
}

const filterRecipients = (recipients: Recipient[], mainRecipient: string | null) =>
  recipients.reduce((acc: string[], { emailAddress }: Recipient) => {
    if (emailAddress !== mainRecipient) {
      acc.push(emailAddress)
    }
    return acc
  }, [])

const noop = () => undefined

const setResolvedAsyncValues = (
  state: LoftRecord,
  values: Partial<Record<keyof LoftAsyncProps, unknown>>
) =>
  state.withMutations(ctx => {
    Object.entries(values).forEach(([key, value]) => {
      ctx.setIn(['async', key as keyof LoftAsyncProps], Promise.resolve(value))
    })
  })

const formatDateForLofting = (
  date: string,
  dateTime: string | null,
  isAllDay: boolean,
  timezoneName: string
) => {
  if (isAllDay) {
    return {
      timezone: timezoneName,
      date: date,
    }
  }
  return {
    timezone: timezoneName,
    date_time: dateTime,
  }
}

export const logAndResolve = (value: null | false | LoftRecord = null) => (
  e: Record<string, unknown> | null = null
) => {
  console.error('resolveWithLog', e, value)
  addBreadcrumb({
    message: 'resolveWithLog',
    category: 'error',
    data: {
      error: e?.toString() || '',
      value,
    },
  })
  return Promise.resolve(value)
}
export const awaitRecipient = async (state: LoftRecord) => {
  const user = await state.getAsync('me').catch(noop)

  const response: Promise<FindByEmailResponse[] | FindByEmailResponse> = await getPersonByEmail(
    state.emailAddress || '',
    {
      features: user?.data._private_fields.features || {},
      user_guid: user?.data.guid || '',
    }
  )

  return state.setAsync('recipient', Array.isArray(response) ? response[0] || null : response)
}

export const loadEmailPreloftData = async (
  state: LoftRecord,
  { gateway, office }: LoftServices
) => {
  const [
    me,
    publicTeamEmailSettings,
    teamPrivacySettings,
    to,
    subject,
    customProperties,
  ] = await Promise.all([
    gateway.me.get(),
    gateway.publicTeamEmailSettings.get(),
    gateway.teamPrivacySettings.get(),
    office.item.getTo(),
    office.item.getSubject(),
    office.item.getCustomPropertyValues({
      templateId: null,
      crmReferences: {},
    }),
  ])

  const { templateId, crmReferences } = customProperties as CustomProperties

  return setResolvedAsyncValues(state, {
    me,
    publicTeamEmailSettings,
    teamPrivacySettings,
    to,
    subject,
    templateId,
    crmReferences,
  })
}

export const loadAppointmentPreloftData = async (state: LoftRecord, { gateway }: LoftServices) => {
  const [me, calendarSettings] = await Promise.all([
    gateway.me.get(),
    gateway.meetings.getCalendarSettings(),
  ])

  return setResolvedAsyncValues(state, {
    me,
    calendarSettings,
  })
}

export const loadAppointmentValidationData = async (
  state: LoftRecord,
  { office }: LoftServices
) => {
  const [attendees, recurrence] = await Promise.all([
    office.item.getAttendees(),
    office.item.getRecurrence(),
  ])

  return setResolvedAsyncValues(state, {
    attendees,
    recurrence,
  })
}

export const loadAppointmentLoftData = async (state: LoftRecord, { office }: LoftServices) => {
  const [
    attachments,
    organizer,
    subject,
    location,
    body,
    startTime,
    endTime,
    itemId,
    customProperties,
  ] = await Promise.all([
    office.item.getAppointmentAttachments(),
    office.item.getOrganizer(),
    office.item.getSubject(),
    office.item.getLocation(),
    office.item.getBody(),
    office.item.getStartTime(),
    office.item.getEndTime(),
    office.item.getItemId(),
    office.item.getCustomPropertyValues({
      crmReferences: {},
      meetingTypeId: null,
      crmCustomFields: {},
      mainGuest: {},
    }),
  ])

  const {
    crmReferences,
    meetingTypeId,
    crmCustomFields,
    mainGuest,
  } = customProperties as CustomProperties

  return setResolvedAsyncValues(state, {
    attachments,
    organizer,
    subject,
    location,
    body,
    startTime,
    endTime,
    itemId,
    crmReferences,
    meetingTypeId,
    crmCustomFields,
    mainGuest,
  })
}

export const loadFrontendEwsMessageContent = async (
  state: LoftRecord,
  { office }: LoftServices
) => {
  const [body, attachments] = await Promise.all([
    office.item.getBody(),
    office.item.getAttachments(),
  ])

  return setResolvedAsyncValues(state, {
    body,
    attachments,
  })
}

export const updateParentEmail = async (state: LoftRecord, { office }: LoftServices) => {
  const ewsMessage = await state.getAsync('ewsMessage')

  if (ewsMessage) {
    const { inReplyTo } = ewsMessage
    const updatePromise = Promise.resolve(office.graphApi.updateParentEmail(inReplyTo))
      .then(result => {
        addBreadcrumb({
          message: 'Graph API: UpdateParentEmail',
          category: 'ms-graph-api',
          data: {
            ewsItemId: state.ewsItemId,
            restItemId: state.restItemId,
            inReplyTo,
            result,
          },
        })

        return result
      })
      .catch(error => {
        addBreadcrumb({
          message: 'Graph API: UpdateParentEmail failed',
          category: 'ms-graph-api',
          level: 'error',
          data: {
            ewsItemId: state.ewsItemId,
            restItemId: state.restItemId,
            inReplyTo,
            error: error?.toString() || '',
          },
        })

        return null
      })

    return state.setAsync('updateParentEmail', updatePromise)
  }

  return state.setAsync('updateParentEmail', Promise.resolve(null))
}

export const sendEmail = async (
  state: LoftRecord,
  { gateway, office }: LoftServices
): Promise<void> => {
  try {
    const ewsMessage = await state.getAsync('ewsMessage')

    if (!state.email) {
      // noinspection ExceptionCaughtLocallyJS
      throw new Error('Tracking Body is not defined')
    }

    if (!ewsMessage) {
      addBreadcrumb({
        message: 'Missing ewsMessage in sendEmail',
        category: 'error',
        data: {
          state,
        },
      })
      throw new Error('missing ewsMessage')
    }

    const recipients = await office.item?.getRecipients()

    await office.graphApi.updateAndSendItem(
      ewsMessage.id,
      state.email.tracking_body,
      state.email.subject,
      recipients || emptyRecipients
    )
  } catch (e) {
    if (e !== 'ErrorIrresolvableConflict') {
      console.error('sendEmail error', e)
      throw e
    }

    await new Promise(resolve => setTimeout(resolve, 5000))
    state = await safeSetMessageIds(state)
    state = await ewsGetMessage(state, { gateway, office })

    return sendEmail(state, { gateway, office })
  }
}

export const setRestMessage = async (state: LoftRecord, { office }: LoftServices) => {
  const teamEmailSettings = await state.getAsync('teamEmailSettings')

  if (teamEmailSettings?.outlook_disallow_rest) {
    office.item.restMessage = null
  } else {
    // @ts-expect-error restMessage is not ts yet
    office.item.restMessage = await office.item.rest
      .getCurrentMessage(state.restItemId || '')
      .catch(logAndResolve(false))
  }
}

export const setMessageIds = async (state: LoftRecord) => {
  try {
    const { restItemId, ewsItemId } = await getMessageIds()

    return state.withMutations(ctx => ctx.set('restItemId', restItemId).set('ewsItemId', ewsItemId))
  } catch (error) {
    addBreadcrumb({
      message: 'Failed to set message IDs',
      category: 'error',
      data: {
        error: error?.toString() || '',
      },
    })
    throw error
  }
}

export const safeSetMessageIds = (state: LoftRecord) =>
  onPromiseTimeout(setMessageIds(state).catch(logAndResolve(state)), logAndResolve(state), 5000)

export const ewsGetMessage = async (state: LoftRecord, { office }: LoftServices) => {
  if (!state.ewsItemId) {
    state = await safeSetMessageIds(state)
  }

  if (!state.ewsItemId) {
    throw new Error('ewsItemId is not defined')
  }

  const ewsMessage = await office.graphApi.waitSyncAndGetItemById(state.ewsItemId)

  return state.setAsync('ewsMessage', ewsMessage)
}

export const setSession = async (state: LoftRecord) => {
  const session: ServerSession = await refreshAuthData()

  return state.set('session', session)
}

export const setRecipientEmail = async (state: LoftRecord) => {
  const to = await state.getAsync('to')
  if (!to || to.length === 0) {
    return state
  }
  const [{ emailAddress }] = to
  return state.set('emailAddress', emailAddress)
}

export const addDraftMailingIdToState = async (state: LoftRecord, { office }: LoftServices) => {
  try {
    const customProperties = await office.item?.getCustomProperties()
    const draftMailingId = await customProperties.get('draftMailingId')

    return state.set('draftMailingId', draftMailingId)
  } catch (e) {
    addBreadcrumb({
      message: 'addDraftMailingIdToState',
      category: 'office-js',
      level: 'error',
      data: e?.toString() || '',
    })
  }
}

export const melodyLoft = async (state: LoftRecord, { gateway, office }: LoftServices) => {
  const [
    me,
    recipients,
    subject,
    body,
    attachments,
    templateId,
    crmReferences,
  ] = await Promise.all([
    state.getAsync('me').catch(noop),
    office.item?.getRecipients(),
    state.getAsync('subject').catch(noop),
    state.getAsync('body').catch(noop),
    state.getAsync('attachments').catch(noop),
    state.getAsync('templateId').catch(noop),
    state.getAsync('crmReferences').catch(noop),
  ])

  const isEmailTrackingEnabled =
    me?.data?._private_fields?.features?.outlook_email_tracking || false

  const response: LoftFromOutlookResponse = await gateway.mailings.loftFromOutlook({
    to: state.emailAddress || '',
    additionalTo: filterRecipients(recipients?.to || [], state.emailAddress),
    subject: subject || '',
    body: body || '',
    attachments: attachments || [],
    templateId: templateId || null,
    crmReferences: crmReferences || {},
    draftMailingId: state.draftMailingId,
    // The gateway type expects Recipient[], but the existing JS implementation
    // sent email-address strings; preserve the runtime shape until the gateway
    // contract is reconciled.
    cc: ((recipients?.cc?.map(({ emailAddress }: Recipient) => emailAddress) ||
      []) as unknown) as Recipient[],
    ...(isEmailTrackingEnabled && {
      openTracking: state.trackOpens,
      clickTracking: state.trackClicks,
    }),
  })

  const [email] = response.emails

  return state.withMutations(ctx => ctx.set('mailingId', parseInt(response.id)).set('email', email))
}

export const getEventData = async (state: LoftRecord, { gateway }: LoftServices) => {
  const itemId = (await state.getAsync('itemId').catch(noop)) || ''

  try {
    const response = await gateway.calendarLofting.getLoftedEvent(itemId)

    return state.setAsync('eventData', response)
  } catch (e) {
    return null
  }
}

const getMainGuest = async (
  state: LoftRecord,
  { office }: LoftServices
): Promise<Recipient | Guest | undefined> => {
  const customMainGuest = await state.getAsync('mainGuest').catch(noop)

  // If we have a valid mainGuest, return it
  if (customMainGuest && customMainGuest.emailAddress) {
    return customMainGuest
  }

  // Fallback: find main guest from attendees if not set in custom properties
  try {
    const userEmail =
      office.userProfile?.emailAddress ||
      (typeof Office !== 'undefined' ? Office.context.mailbox.userProfile.emailAddress : null)
    const attendees = (await state.getAsync('attendees').catch(noop)) || ([] as Recipient[])

    if (!attendees || attendees.length === 0) {
      return
    }

    // Import the helper function for finding valid main guest
    const { findValidMainGuest } = await import('../../services/loftCalendarEvents')
    const validMainGuest = findValidMainGuest(userEmail, attendees, [])

    return validMainGuest || {}
  } catch (e) {
    console.error('Error resolving main guest:', e)
    return
  }
}

export const calendarLoftEvent = async (
  state: LoftRecord,
  loftServices: LoftServices,
  attemptsLeft = 5,
  retryDelay = 3000
): Promise<any> => {
  const { gateway } = loftServices
  try {
    addBreadcrumb({ message: 'CalenderLoft: Loft Calender Event', category: 'appointmentLoft' })

    let calendarSettings = await state.getAsync('calendarSettings').catch(noop)
    const [
      attendees,
      organizer,
      start,
      end,
      itemId,
      eventData,
      meetingTypeId,
      me,
      mainGuest,
      subject,
      location,
      body,
      crmCustomFields,
      attachments,
      crmReferences,
    ] = await Promise.all([
      state.getAsync('attendees').catch(noop),
      state.getAsync('organizer').catch(noop),
      state.getAsync('startTime').catch(noop),
      state.getAsync('endTime').catch(noop),
      state.getAsync('itemId').catch(noop),
      state.getAsync('eventData').catch(noop),
      state.getAsync('meetingTypeId').catch(noop),
      state.getAsync('me').catch(noop),
      getMainGuest(state, loftServices),
      state.getAsync('subject').catch(noop),
      state.getAsync('location').catch(noop),
      state.getAsync('body').catch(noop),
      state.getAsync('crmCustomFields').catch(noop),
      state.getAsync('attachments').catch(noop),
      state.getAsync('crmReferences').catch(noop),
    ])

    const startDateTime = DateTime.fromJSDate(start ? new Date(start) : new Date())
    const endDateTime = DateTime.fromJSDate(end ? new Date(end) : new Date())

    const startDate = startDateTime.toFormat('yyyy-MM-dd')
    const endDate = endDateTime.toFormat('yyyy-MM-dd')

    const startTime = startDateTime.toUTC().toISO()
    const endTime = endDateTime.toUTC().toISO()

    const isCreation = !eventData || !eventData.event_id
    const daysDiff = endDateTime.diff(startDateTime, 'days').days
    const isAllDay = Number.isInteger(daysDiff)
    const timezoneName = startDateTime.zoneName || 'UTC'

    if (!calendarSettings) {
      addBreadcrumb({
        message: 'CalenderLoft: Missing calendar settings, refetching',
        level: 'error',
        category: 'appointmentLoft',
      })

      try {
        calendarSettings = await gateway.meetings.getCalendarSettings()

        return state.setAsync('calendarSettings', calendarSettings)
      } catch (error) {
        addBreadcrumb({
          message: 'CalenderLoft: Error fetching calendar settings',
          level: 'error',
          category: 'appointmentLoft',
          data: error?.toString() || '',
        })
      }
    }

    await gateway.calendarLofting.loft({
      azure_forward_event:
        isCreation &&
        isDesktopWindows() &&
        !me?.data?._private_fields?.features.outlook_windows_classic_onsend_lofting,
      isCreation,
      event_id: itemId,
      calendar_id: calendarSettings?.data.primary_calendar_id || '',
      calendar_type: calendarSettings?.data.calendar_type || '',
      meeting_type_id: meetingTypeId === 'default_personal_meeting' ? null : meetingTypeId,
      title: subject || '',
      recurrence: [],
      location: location || null,
      reminders: { use_default: true },
      description: body || '',
      attendees: attendees?.map(
        ({ organizer: isOrganizer, displayName, emailAddress, optional }) => ({
          organizer: isOrganizer || organizer?.emailAddress === emailAddress || false,
          name: displayName || '',
          email: emailAddress || '',
          optional: !!optional,
          resource: false,
          status:
            isOrganizer || organizer?.emailAddress === emailAddress ? 'accepted' : 'needsAction',
        })
      ),
      rooms: [], // rooms will be added in future
      calendar_owner: organizer?.emailAddress || '',
      mark_busy: true,
      visibility: 'default',
      all_day: isAllDay,
      conference_data: null,
      start: formatDateForLofting(startDate, startTime, isAllDay, timezoneName),
      end: formatDateForLofting(endDate, endTime, isAllDay, timezoneName),
      should_sync_event: true,
      crm_custom_fields: crmCustomFields || {},
      recipient_email: mainGuest?.emailAddress,
      recipient_name: resolveRecipientName(mainGuest),
      recipient_phone: mainGuest && 'phone' in mainGuest ? mainGuest.phone : null,
      recipient_company:
        mainGuest && 'person_company_name' in mainGuest ? mainGuest.person_company_name : null,
      person_id: mainGuest && 'id' in mainGuest ? mainGuest.id : null,
      account_id: getAccountId(mainGuest),
      cadence_id: getCadenceId(mainGuest),
      step_id: getStepId(mainGuest),
      attachments: attachments || [],
      crm_references: crmReferences || {},
    })
  } catch (error) {
    addBreadcrumb({
      message: `CalenderLoft: Loft Calender Event failed. Attempt left: ${attemptsLeft}`,
      level: 'error',
      category: 'appointmentLoft',
      data: error?.toString() || '',
    })
    if (attemptsLeft <= 1) {
      return null
    }
    await new Promise(resolve => setTimeout(resolve, retryDelay))
    return calendarLoftEvent(state, loftServices, attemptsLeft - 1, retryDelay)
  }
}

export const calendarLoft = async (state: LoftRecord, loftServices: LoftServices) => {
  try {
    addBreadcrumb({ message: 'CalenderLoft: Lofting Calender Event', category: 'appointmentLoft' })
    await calendarLoftEvent(state, loftServices)
  } catch (error) {
    addBreadcrumb({
      message: 'CalenderLoft: Error in calendarLoft',
      level: 'error',
      category: 'appointmentLoft',
      data: error?.toString() || '',
    })
  }
}

export const addDraftMailingIdToCustomProperties = async (state: LoftRecord) => {
  try {
    if (!state.draftMailingId || state.draftMailingId !== state.mailingId) {
      await insertOutlookCustomData('draftMailingId', state.mailingId)
    }
  } catch (e) {
    addBreadcrumb({
      message: 'addDraftMailingIdToCustomProperties',
      category: 'office-js',
      level: 'error',
      data: e?.toString() || '',
    })
  }
}

async function isReplyMessage(officeWrapper: OfficeWrapper): Promise<boolean> {
  const item = officeWrapper.item

  // Check if the item has a conversation ID (which indicates it's part of a thread)
  const hasConversationId = item.item.conversationId != null

  // Check if the subject starts with "RE:" (case-insensitive)
  const subject = await item.getSubject()
  const subjectStartsWithRe = /^re:/i.test(subject)

  // Check if there's a parent message ID
  let parentMessageId
  try {
    const customProperties = await item.getCustomProperties()
    parentMessageId = customProperties.get('parentMessageId')
  } catch (e) {
    console.error('Error getting custom properties:', e)
    parentMessageId = null
  }

  // If any of these conditions are true, it's likely a reply
  return hasConversationId || subjectStartsWithRe || parentMessageId != null
}

export const melodyLoftAzure = async (state: LoftRecord, { gateway, office }: LoftServices) => {
  const recipients = await office.item?.getRecipients()
  const originalMessageId: string = office.item.getInternetMessageId()

  let subject = (await state.getAsync('subject')) || ''
  const isReply = await isReplyMessage(office)
  if (isReply && !subject.toLowerCase().startsWith('re:')) {
    subject = `RE: ${subject}`
  }

  try {
    const { restUrl, restToken } = await office.mailbox.restConnection()

    if (!restToken) {
      throw new Error('Failed to obtain NAA access token for loftAzure')
    }

    return gateway.mailings.loftAzure({
      syncId: state.syncId,
      restItemId: state.restItemId,
      templateId: (await state.getAsync('templateId')) || null,
      to: state.emailAddress || '',
      additionalTo: filterRecipients(recipients?.to || [], state.emailAddress),
      subject: (await state.getAsync('subject')) || '',
      platform: office.mailbox.platform,
      crmReferences: (await state.getAsync('crmReferences')) || {},
      inReplyTo: isReply ? originalMessageId : undefined,
      references: isReply ? originalMessageId : undefined,
      restUrl,
      restToken,
    })
  } catch (error) {
    console.error('Error in melodyLoftAzure: ', error)
    addBreadcrumb({
      message: 'MelodyLoftAzure: Error in loftAzure',
      level: 'error',
      category: 'melodyLoftAzure',
      data: error?.toString() || '',
    })
    throw error
  }
}

const generateSyncId = (): string =>
  // @ts-expect-error
  ([1e7] + -1e11).replace(/[018]/g, a => (a ^ ((Math.random() * 16) >> (a / 4))).toString(16))

export const setSyncMark = async (state: LoftRecord, { office }: LoftServices) => {
  if (office.mailbox.platform === PlatformType.OFFICE_ONLINE) {
    return state.set('syncId', '')
  }

  const syncId = generateSyncId()
  await office.item.prependBody(`<a href="#sync-${syncId}">&zwnj;</a>`)
  return state.set('syncId', syncId)
}

export const configureGateway = ({ session }: LoftRecord): void => {
  if (!session) throw new Error('API Urls are not set')

  addBreadcrumb({
    category: 'gateway',
    message: 'Configuring API gateway',
    data: {
      melody_api_url: session.environment.melody_api_url,
      public_api_url: session.environment.public_api_url,
    },
  })

  melodyRequest.setBaseUrl(session.environment.melody_api_url)
  publicRequest.setBaseUrl(session.environment.public_api_url)
}

export const melodyMarkLofted = async ({ mailingId }: LoftRecord, { gateway }: LoftServices) => {
  if (mailingId) {
    return gateway.mailings.sentFromOutlook(mailingId).catch(noop)
  }
}

export const itemClose = (_: LoftRecord, { office }: LoftServices) => office.item.close()

export const appointmentClose = async (state: LoftRecord, { office }: LoftServices) => {
  const eventData = await state.getAsync('eventData').catch(noop)
  const me = await state.getAsync('me').catch(noop)

  if (
    isDesktopWindows() &&
    !me?.data._private_fields.features.outlook_windows_classic_onsend_lofting &&
    (!eventData || !eventData.event_id)
  ) {
    office.item.close()
  }
}

export const loftSuccess = async (state: LoftRecord, { gateway }: LoftServices) => {
  return gateway.stats
    .loftSuccess([
      `email_client_type:${await state.getEmailClientType()}`,
      `strategy:${state.strategy}`,
    ])
    .catch(noop)
}

export const ensureSyncIdSaved = async (state: LoftRecord, { office }: LoftServices) => {
  const body = await office.item.getBody()

  if (
    office.mailbox.platform !== PlatformType.OFFICE_ONLINE &&
    !body.includes(`sync-${state.syncId}`)
  ) {
    console.warn('Platform:', office.mailbox.platform)
    console.error('No sync tag found in the body', state.syncId)
    throw new Error('no sync tag in the body')
  }
}

const isAzure = (emailClientType: string) => emailClientType === 'azure'
const isAzureBackendLoftEnabled = (user?: Me) =>
  Boolean(user?.data?._private_fields?.features.outlook_backend_loft_azure)

export const chooseCalendarLoftingStrategy = async (state: LoftRecord) => {
  return state.set('strategy', CALENDAR_LOFTING)
}

export const chooseStrategy = async (state: LoftRecord, { office, gateway }: LoftServices) => {
  const itemType = await office.item.getItemType()
  if (itemType === 'appointment') {
    return state.set('strategy', CALENDAR_LOFTING)
  }

  if (
    isAzureBackendLoftEnabled(await state.getAsync('me')) &&
    isAzure(await state.getEmailClientType())
  ) {
    try {
      const azure = await gateway.user.azure()
      const emailMatch =
        azure.email_address.toLowerCase() === office.userProfile.emailAddress.toLowerCase()
      if (azure.configured && emailMatch) {
        return state.set('strategy', BACKEND_AZURE)
      }
    } catch (e) {
      return state.set('strategy', FRONTEND_EWS)
    }
  }

  return state.set('strategy', FRONTEND_EWS)
}

export const awaitAsync = (
  key: keyof LoftAsyncProps,
  func: (services: LoftServices) => Promise<unknown> | undefined
) => {
  const saver = async (state: LoftRecord, deps: LoftServices) =>
    state.setAsync(key, await func(deps))

  saver.operation = `awaitAsync: ${key}`
  return saver
}

export const withOperationName = (exportedFunctions: any) => {
  Object.keys(exportedFunctions).forEach(key => {
    if (typeof exportedFunctions[key] === 'function') {
      exportedFunctions[key].operation = key
    }
  })
}

export const startProgress = (_: LoftRecord, { office }: LoftServices) => {
  const loftMessages = getLoftMessages()
  office.notifications.removeAll()
  office.notifications.startProgress(notificationTypes.PROGRESS, loftMessages.preparing)
}

export const updateProgress = (key: keyof LoftMessages) => {
  const updater = (_: LoftRecord, { office }: LoftServices) => {
    return office.notifications.updateProgress(notificationTypes.PROGRESS, getLoftMessages()[key])
  }

  updater.operation = `updateProgress: ${key}`
  return updater
}

export const stopProgress = async (_: LoftRecord, { office }: LoftServices) => {
  const loftMessages = getLoftMessages()
  office.notifications.remove(notificationTypes.PROGRESS)
  const itemType = await office.item.getItemType()
  const appointmentLoftedMessage = isDesktopWindows()
    ? loftMessages.lofted
    : loftMessages.appointmentLofted
  office.notifications.addInfo(
    notificationTypes.INFO,
    itemType === 'appointment' ? appointmentLoftedMessage : loftMessages.lofted
  )
}

export const setUserLocale = async (state: LoftRecord) => {
  const me = await state.getAsync('me').catch(noop)
  const locale = me?.data?._private_fields?.locale
  if (locale) {
    await refreshLocaleData(locale)
  }
}

export const setTracingContext = async (state: LoftRecord) => {
  const user = await state.getAsync('me').catch(noop)

  if (!user) {
    return
  }

  setUser({
    id: String(user.data.id),
    email: user.data.email,
    username: user.data.name,
    user_guid: user.data.guid,
    team_id: user.data.team.id,
    permitted_localities: user?.data?._private_fields?.permitted_localities,
  })

  setContext('user_info', {
    user_guid: user.data.guid,
    team_id: user.data.team.id,
    permitted_localities: user?.data?._private_fields?.permitted_localities,
  })

  addEventProcessor(function(event) {
    if (!user?.data?._private_fields?.permitted_localities?.includes('us')) {
      return null
    }

    return event
  })
}

export const setTrackingPreferences = async (state: LoftRecord) => {
  const user = await state.getAsync('me').catch(noop)

  if (!user) {
    // Default to true if no user data
    return state.withMutations(ctx => ctx.set('trackOpens', true).set('trackClicks', true))
  }

  const recipient = (await state.getAsync('recipient').catch(noop)) || null
  const teamEmailSettings = await state.getAsync('publicTeamEmailSettings').catch(noop)
  const teamPrivacySettings = await state.getAsync('teamPrivacySettings').catch(noop)

  const teamDefaults = {
    openTracking: teamEmailSettings?.open_tracking_default ?? true,
    clickTracking: teamEmailSettings?.click_tracking_default ?? true,
  }

  const isTeamAdmin = user.data.team_admin || false
  const overrideDisabled = calculateOverrideDisabled(teamEmailSettings, isTeamAdmin)

  const personalOverride = {
    openTracking: user.data._private_fields?.settings?.personal_open_tracking_override,
    clickTracking: user.data._private_fields?.settings?.personal_click_tracking_override,
  }

  const preferences = resolveTrackingPreferences(teamDefaults, {
    overrideDisabled,
    personalOverride,
    recipient,
    teamPrivacySettings,
  })

  return state.withMutations(ctx =>
    ctx.set('trackOpens', preferences.trackOpens).set('trackClicks', preferences.trackClicks)
  )
}
